/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import axios from 'axios';
import { environment } from '@/environments/environment';
import { ProjectsResponse } from '@/app/interfaces/projects';
import { DeploymentResponse, AiSystemDeployment, AiSystemDeploymentToDto } from '@/app/interfaces/aiSystemDeployment';
import { ToastService } from '../script/toast.service';
import { LoaderService } from '@/app/services/interceptor/loader-service';
import { DeploySystemDetail } from '@/app/interfaces/deploy-system-of-ai-detail';
import {
  DeploySystemReviewDetail,
  DeploySystemReviewPayload,
  DeploySystemReviewRoleBack,
} from '@/app/interfaces/deploy-system-review';
import {
  DeploySystemCorrectionDetail,
  DeploySystemCorrectionDetailRequest,
  DeploySystemCorrectionSubmitPayload,
  DeploySystemCorrectionSubmitResponse,
  deploySystemCorrectionDetailContext,
} from '@/app/interfaces/deploy-system-correction';
import { PermitSynchronizeService } from './permit-synchronize.service';
import { SystemsResponse, SystemsResponseDetails } from '@/app/interfaces/systems';
import {
  SystemReviewDetail,
  SystemReviewDetailRequest,
  SystemReviewPayload,
  SystemReviewRoleBack,
} from '@/app/interfaces/system-review';
import {
  SystemCorrectionDetail,
  SystemCorrectionDetailRequest,
  SystemCorrectionSubmitPayload,
  SystemCorrectionSubmitResponse,
} from '@/app/interfaces/system-correction';

@Injectable({
  providedIn: 'root',
})
export class SystemsService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private toastService: ToastService,
    private loader: LoaderService,
    private permitSynchronizeService: PermitSynchronizeService
  ) {}

  async getSystemsList(filters, jobcode): Promise<SystemsResponse> {
    try {
      this.loader.show();
      const orgCode = this.getServiceManagerOrgCodes();
      const response = await axios.post<SystemsResponse>(
        `${environment.url}/api/aurora/listado`,
        { filters, jobcode, list: 'systems', orgCode },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      this.toastService.showError(
        'Error al obtener el listado de sistema',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  private getServiceManagerOrgCodes(): string[] {
    const rawUserData = localStorage.getItem('userData');

    if (!rawUserData) {
      return [];
    }

    try {
      const userData = JSON.parse(rawUserData) as { roles?: Record<string, string[]> };
      const serviceManagerOrgs = userData.roles?.jefe_servicio;

      if (!Array.isArray(serviceManagerOrgs)) {
        return [];
      }

      return serviceManagerOrgs.map((orgCode) => this.normalizeWorkflowOrgCode(orgCode));
    } catch {
      return [];
    }
  }

  private normalizeWorkflowOrgCode(orgCode: string): string {
    const normalizedOrgCode = orgCode.match(/ORG\d+$/);

    return normalizedOrgCode ? normalizedOrgCode[0] : orgCode;
  }

  async getSystemDetails(id_system, detail) {
    try {
      this.loader.show();
      const { data } = await axios.post<SystemsResponseDetails>(
        `${environment.url}/api/aurora/detalle`,
        { id_sistema: id_system, detail: detail },
        {
          withCredentials: true,
        }
      );
      return data.data;
    } catch (err) {
      this.toastService.showError(
        'Error al obtener el detalle del sistema',
        err?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw err;
    } finally {
      this.loader.hide();
    }
  }

  async getSystemReviewDetail(idSystem: string, role: SystemReviewRoleBack): Promise<SystemReviewDetail> {
    try {
      this.loader.show();
      const payload: SystemReviewDetailRequest = {
        detail: 'system',
        id_sistema: idSystem,
        role,
      };

      const { data } = await axios.post<{ data: SystemReviewDetail; message: string }>(
        `${environment.url}/api/aurora/detalle`,
        payload,
        {
          withCredentials: true,
        }
      );

      return data.data;
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;

      this.toastService.showError(
        'Error al obtener el detalle de revisión del sistema',
        errorMessage || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async getSystemCorrectionDetail(idSystem: string): Promise<SystemCorrectionDetail> {
    try {
      this.loader.show();
      const payload: SystemCorrectionDetailRequest = {
        detail: 'system',
        id_sistema: idSystem,
        role: 'CORRECTION',
      };

      const { data } = await axios.post<{ data: SystemCorrectionDetail; message: string }>(
        `${environment.url}/api/aurora/detalle`,
        payload,
        {
          withCredentials: true,
        }
      );

      return data.data;
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;

      this.toastService.showError(
        'Error al obtener el detalle de subsanación del sistema',
        errorMessage || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async createNewSystem(body): Promise<ProjectsResponse> {
    try {
      this.loader.show();
      const response = await axios.post<ProjectsResponse>(
        `${environment.url}/api/aurora/validar_guardar_sistema_IA`,
        body,
        {
          withCredentials: true,
        }
      );
      this.permitSynchronizeService.synchronizePermissions(
        undefined,
        body?.jobCode,
        body?.contactEmailResponsible,
        undefined,
        body?.orgCode
      );
      this.toastService.showSuccess('Sistema registrado', 'El sistema se ha registrado correctamente');
      return response.data;
    } catch (error) {
      this.toastService.showError(
        'Error al dar de alta el sistema.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async createDeployment(deploymentData: AiSystemDeployment & { orgCode?: string }): Promise<DeploymentResponse> {
    try {
      this.loader.show();
      const deploymentDto = {
        ...AiSystemDeploymentToDto(deploymentData),
        orgCode: deploymentData.orgCode,
      };
      const response = await axios.post<DeploymentResponse>(
        `${environment.url}/api/aurora/validar_guardar_ejecucion_sistema_IA`,
        deploymentDto,
        {
          withCredentials: true,
        }
      );
      this.toastService.showSuccess('Sistema desplegado', 'El despliegue del sistema se ha registrado correctamente');
      this.permitSynchronizeService.synchronizePermissions();
      return response.data;
    } catch (error) {
      this.toastService.showError(
        'Error al crear el despliegue del sistema.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async getSystemExecutionDetails(id_system: string): Promise<DeploySystemDetail> {
    try {
      this.loader.show();

      const { data } = await axios.post<{ data: DeploySystemDetail; message: string }>(
        `${environment.url}/api/aurora/detalle`,
        {
          detail: 'execution',
          id_sistema: id_system,
        },
        {
          withCredentials: true,
        }
      );
      return data.data;
    } catch (error) {
      this.toastService.showError(
        'Error al obtener el detalle de la ejecución del sistema.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async getDeploySystemReviewDetail(
    idSystem: string,
    role?: DeploySystemReviewRoleBack
  ): Promise<DeploySystemReviewDetail> {
    try {
      this.loader.show();
      const payload = role
        ? { detail: 'execution', id_sistema: idSystem, role }
        : { detail: 'execution', id_sistema: idSystem };

      const { data } = await axios.post<{ data: DeploySystemReviewDetail; message: string }>(
        `${environment.url}/api/aurora/detalle`,
        payload,
        {
          withCredentials: true,
        }
      );
      return data.data;
    } catch (error) {
      this.toastService.showError(
        'Error al obtener el detalle de revisión del despliegue.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async getDeploySystemCorrectionDetail(idSystem: string): Promise<DeploySystemCorrectionDetail> {
    try {
      this.loader.show();
      const payload: DeploySystemCorrectionDetailRequest = {
        detail: 'execution',
        id_sistema: idSystem,
        role: deploySystemCorrectionDetailContext,
      };

      const { data } = await axios.post<{ data: DeploySystemCorrectionDetail; message: string }>(
        `${environment.url}/api/aurora/detalle`,
        payload,
        {
          withCredentials: true,
        }
      );

      return data.data;
    } catch (error) {
      this.toastService.showError(
        'Error al obtener el detalle de subsanación del despliegue.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async submitDeploySystemCorrection(
    payload: DeploySystemCorrectionSubmitPayload
  ): Promise<DeploySystemCorrectionSubmitResponse> {
    try {
      this.loader.show();

      const { data } = await axios.post<DeploySystemCorrectionSubmitResponse>(
        `${environment.url}/api/aurora/subsanar_ejecucion_sistema_IA`,
        payload,
        {
          withCredentials: true,
        }
      );

      this.toastService.showSuccess(
        'Subsanación enviada',
        data.body?.message ?? 'Despliegue de sistema subsanado correctamente.'
      );

      return data;
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;

      this.toastService.showError(
        'Error al enviar la subsanación del despliegue.',
        errorMessage || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async submitSystemCorrection(payload: SystemCorrectionSubmitPayload): Promise<SystemCorrectionSubmitResponse> {
    try {
      this.loader.show();

      const { data } = await axios.post<SystemCorrectionSubmitResponse>(
        `${environment.url}/api/aurora/subsanar_sistema_IA`,
        payload,
        {
          withCredentials: true,
        }
      );

      this.toastService.showSuccess(
        'Subsanación enviada',
        data.body?.message ?? 'Sistema de IA subsanado correctamente.'
      );

      return data;
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;

      this.toastService.showError(
        'Error al enviar la subsanación del sistema.',
        errorMessage || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async changeDeploySystemReviewState(payload: DeploySystemReviewPayload): Promise<unknown> {
    try {
      this.loader.show();

      const { data } = await axios.post(`${environment.url}/api/aurora/cambio_estado_ejecucion_sistema`, payload, {
        withCredentials: true,
      });

      this.toastService.showSuccess(
        'Revisión enviada correctamente',
        'La revisión del despliegue se ha enviado con éxito'
      );

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al enviar la revisión',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async changeSystemReviewState(payload: SystemReviewPayload): Promise<unknown> {
    try {
      this.loader.show();

      const { data } = await axios.post<unknown>(`${environment.url}/api/aurora/cambio_estado_sistema`, payload, {
        withCredentials: true,
      });

      this.toastService.showSuccess(
        'Revisión enviada correctamente',
        'La revisión del sistema se ha enviado con éxito'
      );

      return data;
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;

      this.toastService.showError('Error al enviar la revisión', errorMessage || 'Ha ocurrido un error inesperado');
      throw error;
    } finally {
      this.loader.hide();
    }
  }
}
